<script setup lang="ts">
import { computed } from 'vue';
import { resolveEmojiCardContent } from './emoji-card-content';
import Popover from '../Popover/Popover.vue';
import TEmojiPicker from '../Emoji/TEmojiPicker.vue';
import type { TEmoji } from '../Emoji/t-emoji';
import type { EmojiCardProps } from './emoji-card';

const props = withDefaults(defineProps<EmojiCardProps>(), {
  readonly: false,
  previewSize: 'default',
});

const emit = defineEmits<{
  // eslint-disable-next-line @typescript-eslint/naming-convention
  'update:emoji': [value: string];
}>();

const content = computed(() =>
  resolveEmojiCardContent({
    emoji: props.emoji,
    imageUrl: props.imageUrl,
  }),
);

const previewSizeClass = computed(() =>
  props.previewSize === 'large' ? 'size-8 text-3xl' : 'size-6 text-2xl',
);

const selectEmoji = (newEmoji: TEmoji, close: () => void): void => {
  emit('update:emoji', newEmoji.colons);
  close();
};
</script>

<template>
  <div>
    <Popover v-if="!props.readonly" placement="bottom-start" no-padding>
      <template #trigger>
        <div
          class="flex h-12 w-12 cursor-pointer items-center justify-center rounded-md bg-white shadow-sm transition-all hover:shadow-md"
        >
          <div
            v-if="content.imageUrl"
            :style="'background-image: url(' + content.imageUrl + ')'"
            class="bg-contain bg-center bg-no-repeat"
            :class="previewSizeClass"
          />
          <span
            v-else-if="content.emoji"
            class="inline-flex items-center justify-center leading-none select-none"
            :class="previewSizeClass"
            aria-hidden="true"
          >
            {{ content.emoji }}
          </span>
        </div>
      </template>

      <template #content="{ close }">
        <div class="relative min-w-max overflow-x-hidden">
          <TEmojiPicker
            @emoji-selected="(emoji) => selectEmoji(emoji, close)"
          />
        </div>
      </template>
    </Popover>
    <div
      v-else
      class="flex h-12 w-12 items-center justify-center rounded-md bg-white shadow-sm"
    >
      <div
        v-if="content.imageUrl"
        :style="'background-image: url(' + content.imageUrl + ')'"
        class="bg-contain bg-center bg-no-repeat"
        :class="previewSizeClass"
      />
      <span
        v-else-if="content.emoji"
        class="inline-flex items-center justify-center leading-none select-none"
        :class="previewSizeClass"
        aria-hidden="true"
      >
        {{ content.emoji }}
      </span>
    </div>
  </div>
</template>
